/**
 * Putting a picture into a document: straight to S3, never through the API.
 *
 * THREE STEPS, AND THE MIDDLE ONE DOES NOT TOUCH OUR SERVERS.
 *
 *   POST /t11/v1/document_image   reserve the figure; answers with one presigned URL per part
 *   PUT  <presigned url>          the bytes, from the browser to the bucket
 *   PUT  /t11/v1/document_image   report the ETags; links the figure and answers with a display URL
 *
 * The same shape t1 uses for a record's files, and the same reason: a 6MB lambda limit means a
 * photograph cannot come through the API, and a file that could would still be billed as if it had.
 *
 * REFUSED EARLY, IN WORDS. The server checks all of this again -- it has to, since a browser is not
 * a security boundary -- but a person who picked a 40MB TIFF should be told so before the upload
 * starts rather than after it fails.
 */

/** Mirrors ALLOWED_MIME. SVG is deliberately absent: the signed rendition cannot draw one. */
export const ACCEPTED = ["image/png", "image/jpeg", "image/gif", "image/webp"];

/** Mirrors MAX_BYTES. A figure, not a film. */
export const MAX_BYTES = 25 * 1024 * 1024;

/** The `accept` attribute for the file picker, so the dialog filters before anybody chooses. */
export const ACCEPT_ATTRIBUTE = ACCEPTED.join(",");

export function rejectionReason(file: File): string | null {
    if (!ACCEPTED.includes((file.type || "").toLowerCase())) {
        return `${file.name} is not an image this document can carry. Use a PNG, JPEG, GIF or WebP.`;
    }
    if (file.size <= 0) {
        return `${file.name} is empty.`;
    }
    if (file.size > MAX_BYTES) {
        return `${file.name} is ${(file.size / (1024 * 1024)).toFixed(1)}MB. A figure may be at most 25MB.`;
    }
    return null;
}

/** What the picture measures, so the figure can be inserted at a sensible width on the first paint. */
export function measure(file: File): Promise<{width: number; height: number} | null> {
    return new Promise(resolve => {
        const url = URL.createObjectURL(file);
        const probe = new Image();
        probe.onload = () => {
            resolve({width: probe.naturalWidth, height: probe.naturalHeight});
            URL.revokeObjectURL(url);
        };
        probe.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
        probe.src = url;
    });
}

/**
 * Upload one file and return what the editor needs to insert it.
 *
 * `axios` is the session-carrying client; the presigned PUTs deliberately use bare `fetch`, because
 * a presigned URL carries its own authorisation and an Authorization header alongside it is what S3
 * rejects the request for.
 */
export async function uploadFigure(
    axios: any,
    documentUSID: string,
    file: File,
): Promise<{attachment: string; url: string; width: number | null}> {
    const reason = rejectionReason(file);
    if (reason) throw new Error(reason);

    // THE PERSONA IS NOT OPTIONAL -- both calls make the server ask t1 whether this person may see
    // the revision -- but it is no longer passed here: CresAxios puts it on every request, which is
    // the only way twenty-odd call sites cannot forget it. See its request interceptor.
    const begun = (await axios.post("/t11/v1/document_image", {
        USID: documentUSID,
        Name: file.name,
        Bytes: file.size,
        MimeType: file.type,
    })).data;

    const partSize: number = begun.PartSize;
    const urls: string[] = begun.PartUrls || [];
    if (!urls.length) throw new Error("The server did not offer anywhere to upload to.");

    const parts: {PartNumber: number; ETag: string}[] = [];
    for (let index = 0; index < urls.length; index++) {
        const slice = file.slice(index * partSize, Math.min((index + 1) * partSize, file.size));
        const response = await fetch(urls[index], {method: "PUT", body: slice});
        if (!response.ok) {
            throw new Error(`Part ${index + 1} of ${urls.length} failed to upload (${response.status}).`);
        }
        // THE ETAG COMES BACK IN A HEADER THE BUCKET HAS TO EXPOSE. Without `expose_headers: ETag`
        // in the bucket's CORS this reads null in the browser and the upload can never be completed
        // -- with no error anywhere except here.
        const etag = response.headers.get("ETag");
        if (!etag) {
            throw new Error("S3 did not return an ETag. The bucket's CORS must expose that header.");
        }
        parts.push({PartNumber: index + 1, ETag: etag.replace(/"/g, "")});
    }

    const finished = (await axios.put("/t11/v1/document_image", {
        AttachmentUSID: begun.AttachmentUSID,
        Parts: parts,
        USID: documentUSID,
    })).data;

    const size = await measure(file);
    return {
        attachment: finished.AttachmentUSID,
        url: finished.Url,
        // INSERTED AT ITS OWN SIZE, CAPPED TO THE TEXT COLUMN. A 4000px photograph inserted at 4000px
        // would be scaled down by the stylesheet on screen and clamped by the renderer in the PDF --
        // two different answers for one figure. Deciding it once, here, keeps them the same.
        width: size ? Math.min(size.width, 624) : null,
    };
}
